import { Timestamp } from 'firebase/firestore';

/**
 * Parses a value into a number, handling currency symbols, commas, and percentages.
 * Returns null if parsing fails.
 */
export const parseNumericValue = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    // Remove currency symbols, percentage signs, commas
    const cleanValue = String(value).replace(/[$,%]/g, '').replace(/,/g, '');
    if (cleanValue === '') return null; // Handle case where input was only symbols
    const num = parseFloat(cleanValue);
    return isNaN(num) ? null : num;
};

/**
 * Helper function to format a Date object or Firestore Timestamp as MM/DD/YYYY.
 * Uses UTC methods to avoid timezone issues.
 * Returns '-' if input is invalid.
 */
export const formatDateMMDDYYYY = (dateInput: Date | Timestamp | null): string => {
    let date: Date | null = null;
    if (dateInput instanceof Timestamp) {
        try {
            date = dateInput.toDate();
        } catch (e) { date = null; }
    } else if (dateInput instanceof Date) {
        date = dateInput;
    }

    if (!date || isNaN(date.getTime())) return '-';

    try {
        // Use UTC methods to ensure consistency regardless of local timezone
        const month = (date.getUTCMonth() + 1).toString(); // No padding needed
        const day = date.getUTCDate().toString();
        const year = date.getUTCFullYear().toString().slice(-2); // Get last 2 digits
        return `${month}/${day}/${year}`;
    } catch (error) {
        console.error("Error formatting date:", error);
        return '-';
    }
};

/**
 * Converts an Excel serial date number to a JavaScript Date object.
 * Assumes the standard Excel base date (December 30, 1899).
 * Does not handle the 1900 leap year bug present in some Excel versions.
 * @param serial The Excel serial date number.
 * @returns A Date object or null if input is invalid.
 */
export const excelSerialDateToJSDate = (serial: number | string | null | undefined): Date | null => {
    const numSerial = typeof serial === 'number' ? serial : parseNumericValue(serial);

    if (numSerial === null || numSerial < 1) {
        // Excel serial dates must be positive numbers.
        // 0 corresponds to Jan 0, 1900 which is Dec 31, 1899. 1 is Jan 1, 1900.
        return null;
    }

    try {
        // Excel base date: December 30, 1899 (serial number 0 in Excel's 1900 date system)
        // JavaScript base date: January 1, 1970 UTC
        // Days between 12/30/1899 and 1/1/1970 = 25569 (including the erroneous 1900 leap day)
        // Excel treats serial 1 as Jan 1, 1900.
        // We subtract 2 because JS dates are 0-indexed and Excel's base is effectively Dec 30, 1899.
        const excelEpochDiff = 25569;
        const daysSinceEpoch = numSerial - excelEpochDiff;
        const millisecondsSinceEpoch = daysSinceEpoch * 24 * 60 * 60 * 1000;

        // Create date - this will be UTC milliseconds since epoch
        const date = new Date(millisecondsSinceEpoch);

        // Basic validation
        if (isNaN(date.getTime())) {
            return null;
        }
        return date;
    } catch (e) {
        console.error("Error converting Excel date:", e);
        return null;
    }
};

/**
 * Parses various date inputs (string, number, Timestamp) into a JavaScript Date object.
 * Handles common string formats (MM/DD/YYYY, YYYY-MM-DD) and Excel serial numbers.
 * @param dateInput The value to parse.
 * @returns A Date object or null if parsing fails.
 */
export const parseDate = (dateInput: any): Date | null => {
    if (!dateInput) return null;

    if (dateInput instanceof Timestamp) {
        try {
            return dateInput.toDate();
        } catch (e) { return null; }
    }

    if (dateInput instanceof Date) {
        return !isNaN(dateInput.getTime()) ? dateInput : null;
    }

    if (typeof dateInput === 'number') {
        // Assume it's an Excel serial date
        return excelSerialDateToJSDate(dateInput);
    }

    if (typeof dateInput === 'string') {
        // Try parsing common string formats
        try {
            // Attempt direct parsing (handles ISO 8601 like YYYY-MM-DD)
            let date = new Date(dateInput);
            if (!isNaN(date.getTime())) return date;

            // Attempt MM/DD/YYYY or MM/DD/YY
            const parts = dateInput.split('/');
            if (parts.length === 3) {
                const month = parseInt(parts[0], 10) - 1;
                const day = parseInt(parts[1], 10);
                let year = parseInt(parts[2], 10);
                if (year < 100) { // Handle YY format
                    year += 2000; // Assume 21st century
                }
                if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
                    date = new Date(Date.UTC(year, month, day)); // Use UTC to avoid timezone shifts
                    if (!isNaN(date.getTime())) return date;
                }
            }

            // Add other string format checks if necessary (e.g., DD-MMM-YYYY)

        } catch (e) {
            // Parsing failed
        }
    }

    // If all parsing attempts fail
    console.warn(`Could not parse date input: ${dateInput}`);
    return null;
};


// Add other shared utilities here if needed in the future.